/**
 * Health Insights Component
 * AI-powered health analysis with report cards, recommendations, and sharing
 */
import React, { useState, useEffect } from 'react';
import { 
  Brain, TrendingUp, TrendingDown, Minus, AlertTriangle,
  Heart, Activity, Target, CheckCircle2, Circle, Share2,
  Download, Copy, Check, ChevronRight, Sparkles, Calendar,
  ArrowLeft, RefreshCw, FileText, Link2, X
} from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Score color helper
const getScoreColor = (score) => {
  if (score >= 80) return 'text-green-400';
  if (score >= 60) return 'text-yellow-400';
  if (score >= 40) return 'text-orange-400';
  return 'text-red-400';
};

const getScoreBg = (score) => {
  if (score >= 80) return 'from-green-600/20 to-green-600/5 border-green-500/30';
  if (score >= 60) return 'from-yellow-600/20 to-yellow-600/5 border-yellow-500/30';
  if (score >= 40) return 'from-orange-600/20 to-orange-600/5 border-orange-500/30';
  return 'from-red-600/20 to-red-600/5 border-red-500/30';
};

const getStatusIcon = (status) => {
  switch (status) {
    case 'good': return <CheckCircle2 className="w-5 h-5 text-green-400" />;
    case 'fair': return <Circle className="w-5 h-5 text-yellow-400" />;
    case 'needs_attention': return <AlertTriangle className="w-5 h-5 text-red-400" />;
    default: return <Circle className="w-5 h-5 text-gray-400" />;
  }
};

const HealthInsights = ({ token, isPreview = false, onClose, onViewFull }) => {
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [insight, setInsight] = useState(null);
  const [pastInsights, setPastInsights] = useState([]);
  const [selectedPeriod, setSelectedPeriod] = useState('weekly');
  const [showShareModal, setShowShareModal] = useState(false);
  const [shareLink, setShareLink] = useState('');
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (token) {
      fetchLatestInsight();
      fetchPastInsights();
    }
  }, [token, selectedPeriod]);

  const fetchLatestInsight = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/sankat-mochan/health/insights/latest?period=${selectedPeriod}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setInsight(data);
      }
    } catch (error) {
      console.error('Error fetching insight:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchPastInsights = async () => {
    try {
      const res = await fetch(`${API_URL}/api/sankat-mochan/health/insights?limit=5`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setPastInsights(data.insights || []);
      }
    } catch (error) {
      console.error('Error fetching past insights:', error);
    }
  };

  const generateNewInsight = async () => {
    setGenerating(true);
    try {
      const formData = new FormData();
      formData.append('period', selectedPeriod);
      formData.append('force', 'true');
      
      const res = await fetch(`${API_URL}/api/sankat-mochan/health/insights/generate`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` },
        body: formData
      });
      
      if (res.ok) {
        const data = await res.json();
        setInsight(data);
        toast.success('New insight generated!');
        fetchPastInsights();
      } else {
        toast.error('Failed to generate insight');
      }
    } catch (error) {
      toast.error('Error generating insight');
    } finally {
      setGenerating(false);
    }
  };

  const toggleSharing = async () => {
    if (!insight) return;
    
    try {
      const formData = new FormData();
      formData.append('enable', (!insight.is_shared).toString());
      
      const res = await fetch(`${API_URL}/api/sankat-mochan/health/insights/${insight.id}/share`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` },
        body: formData
      });
      
      if (res.ok) {
        const data = await res.json();
        setInsight(prev => ({ ...prev, is_shared: data.is_shared, share_token: data.share_token }));
        if (data.is_shared) {
          setShareLink(`${window.location.origin}/health-insight/${data.share_token}`);
          setShowShareModal(true);
        }
        toast.success(data.is_shared ? 'Sharing enabled!' : 'Sharing disabled');
      }
    } catch (error) {
      toast.error('Failed to toggle sharing');
    }
  };

  const copyShareLink = () => {
    navigator.clipboard.writeText(shareLink);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
    toast.success('Link copied!');
  };

  const exportPDF = async () => {
    if (!insight) return;
    
    try {
      const res = await fetch(`${API_URL}/api/sankat-mochan/health/insights/pdf/${insight.id}`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (res.ok) {
        const blob = await res.blob();
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `health_insight_${selectedPeriod}_${new Date().toISOString().split('T')[0]}.pdf`;
        a.click();
        URL.revokeObjectURL(url);
        toast.success('PDF downloaded!');
      } else {
        toast.error('PDF export not available');
      }
    } catch (error) {
      toast.error('Failed to export PDF');
    }
  };

  // Preview Card Component
  if (isPreview) {
    return (
      <div 
        onClick={onViewFull}
        className="bg-gradient-to-br from-violet-600/30 to-fuchsia-600/30 rounded-2xl p-6 cursor-pointer hover:scale-105 transition-all border border-violet-500/30"
        data-testid="health-insights-card"
      >
        <Brain className="w-12 h-12 text-violet-400 mb-4" />
        <h3 className="text-xl font-bold text-white mb-2">Health Insights</h3>
        <p className="text-gray-400 text-sm mb-4">
          AI-powered analysis & personalized recommendations
        </p>
        {insight && (
          <div className="flex items-center gap-3">
            <div className={`text-2xl font-bold ${getScoreColor(insight.overall_score)}`}>
              {insight.overall_score}/100
            </div>
            <span className="text-xs text-gray-400">Overall Score</span>
          </div>
        )}
        <div className="flex items-center text-violet-400 mt-2">
          <span>View Insights</span>
          <ChevronRight className="w-5 h-5" />
        </div>
      </div>
    );
  }

  // Full Insights View
  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-900 via-slate-800 to-slate-900 pb-20">
      {/* Header */}
      <div className="sticky top-0 z-10 bg-slate-900/90 backdrop-blur-lg border-b border-slate-800">
        <div className="max-w-4xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between mb-4">
            <button onClick={onClose} className="text-white flex items-center gap-2">
              <ArrowLeft className="w-5 h-5" /> Back
            </button>
            <h1 className="text-xl font-bold text-white flex items-center gap-2">
              <Brain className="w-6 h-6 text-violet-400" />
              Health Insights
            </h1>
            <div className="flex gap-2">
              <button
                onClick={exportPDF}
                className="p-2 bg-slate-800 rounded-lg text-gray-400 hover:text-white"
                title="Export PDF"
              >
                <Download className="w-5 h-5" />
              </button>
              <button
                onClick={toggleSharing}
                className={`p-2 rounded-lg ${insight?.is_shared ? 'bg-violet-500 text-white' : 'bg-slate-800 text-gray-400 hover:text-white'}`}
                title="Share"
              >
                <Share2 className="w-5 h-5" />
              </button>
            </div>
          </div>
          
          {/* Period Selector */}
          <div className="flex items-center gap-2">
            <button
              onClick={() => setSelectedPeriod('weekly')}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${
                selectedPeriod === 'weekly' ? 'bg-violet-500 text-white' : 'bg-slate-800 text-gray-400'
              }`}
            >
              Weekly
            </button>
            <button
              onClick={() => setSelectedPeriod('monthly')}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${
                selectedPeriod === 'monthly' ? 'bg-violet-500 text-white' : 'bg-slate-800 text-gray-400'
              }`}
            >
              Monthly
            </button>
            <button
              onClick={generateNewInsight}
              disabled={generating}
              className="ml-auto flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-violet-600 to-fuchsia-600 text-white rounded-lg text-sm font-medium"
            >
              {generating ? (
                <><RefreshCw className="w-4 h-4 animate-spin" /> Generating...</>
              ) : (
                <><Sparkles className="w-4 h-4" /> Generate New</>
              )}
            </button>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-4xl mx-auto px-4 py-6">
        {loading ? (
          <div className="flex items-center justify-center py-20">
            <div className="animate-spin w-8 h-8 border-4 border-violet-500 border-t-transparent rounded-full" />
          </div>
        ) : !insight ? (
          <div className="text-center py-20">
            <Brain className="w-16 h-16 mx-auto mb-4 text-gray-600" />
            <p className="text-gray-400 mb-4">No insights yet</p>
            <button
              onClick={generateNewInsight}
              className="px-6 py-3 bg-violet-500 text-white rounded-xl font-medium"
            >
              Generate Your First Insight
            </button>
          </div>
        ) : (
          <div className="space-y-6">
            {/* Overall Score Card */}
            <div className={`bg-gradient-to-br ${getScoreBg(insight.overall_score)} rounded-2xl p-6 border`}>
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-gray-400 text-sm mb-1">Overall Health Score</p>
                  <p className={`text-5xl font-bold ${getScoreColor(insight.overall_score)}`}>
                    {insight.overall_score}
                    <span className="text-2xl text-gray-400">/100</span>
                  </p>
                </div>
                <div className="text-right">
                  <p className="text-gray-400 text-sm">
                    {insight.period?.charAt(0).toUpperCase() + insight.period?.slice(1)} Report
                  </p>
                  <p className="text-gray-500 text-xs">
                    {new Date(insight.generated_at).toLocaleDateString()}
                  </p>
                  {insight.comparison?.wellness_change !== 0 && (
                    <div className={`flex items-center justify-end gap-1 mt-2 ${
                      insight.comparison.is_improving ? 'text-green-400' : 'text-red-400'
                    }`}>
                      {insight.comparison.is_improving ? <TrendingUp className="w-4 h-4" /> : <TrendingDown className="w-4 h-4" />}
                      <span className="text-sm">{Math.abs(insight.comparison.wellness_change).toFixed(1)} from last period</span>
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Score Breakdown */}
            {insight.score_breakdown && (
              <div className="bg-slate-800/50 rounded-2xl p-6">
                <h3 className="text-lg font-bold text-white mb-4">Score Breakdown</h3>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  {Object.entries(insight.score_breakdown).map(([key, value]) => (
                    <div key={key} className="text-center">
                      <div className={`text-2xl font-bold ${getScoreColor(value)}`}>{value}</div>
                      <p className="text-gray-400 text-sm capitalize">{key}</p>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Summary */}
            <div className="bg-slate-800/50 rounded-2xl p-6">
              <h3 className="text-lg font-bold text-white mb-3 flex items-center gap-2">
                <FileText className="w-5 h-5 text-violet-400" /> Summary
              </h3>
              <p className="text-gray-300">{insight.summary}</p>
            </div>

            {/* Health Areas */}
            {insight.health_areas && (
              <div className="bg-slate-800/50 rounded-2xl p-6">
                <h3 className="text-lg font-bold text-white mb-4">Health Areas</h3>
                <div className="space-y-3">
                  {Object.entries(insight.health_areas).map(([area, data]) => (
                    <div key={area} className="flex items-center justify-between p-3 bg-slate-900/50 rounded-xl">
                      <div className="flex items-center gap-3">
                        {getStatusIcon(data.status)}
                        <span className="text-white capitalize">{area}</span>
                      </div>
                      <span className="text-gray-400 text-sm">{data.note}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Key Findings */}
            {insight.key_findings?.length > 0 && (
              <div className="bg-slate-800/50 rounded-2xl p-6">
                <h3 className="text-lg font-bold text-white mb-4 flex items-center gap-2">
                  <Target className="w-5 h-5 text-blue-400" /> Key Findings
                </h3>
                <ul className="space-y-2">
                  {insight.key_findings.map((finding, idx) => (
                    <li key={idx} className="flex items-start gap-2 text-gray-300">
                      <span className="text-blue-400 mt-1">•</span>
                      {finding}
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {/* Predictions */}
            {insight.predictions?.length > 0 && (
              <div className="bg-gradient-to-br from-purple-600/20 to-purple-600/5 rounded-2xl p-6 border border-purple-500/30">
                <h3 className="text-lg font-bold text-white mb-4 flex items-center gap-2">
                  <Sparkles className="w-5 h-5 text-purple-400" /> Predictions
                </h3>
                <ul className="space-y-2">
                  {insight.predictions.map((pred, idx) => (
                    <li key={idx} className="flex items-start gap-2 text-gray-300">
                      <TrendingUp className="w-4 h-4 text-purple-400 mt-1" />
                      {pred}
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {/* Recommendations */}
            {insight.recommendations?.length > 0 && (
              <div className="bg-slate-800/50 rounded-2xl p-6">
                <h3 className="text-lg font-bold text-white mb-4 flex items-center gap-2">
                  <Heart className="w-5 h-5 text-pink-400" /> Recommendations
                </h3>
                <div className="space-y-3">
                  {insight.recommendations.map((rec, idx) => {
                    const text = typeof rec === 'string' ? rec : rec.text;
                    const priority = typeof rec === 'object' ? rec.priority : 'medium';
                    const priorityColors = {
                      high: 'bg-red-500/20 text-red-400 border-red-500/30',
                      medium: 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30',
                      low: 'bg-green-500/20 text-green-400 border-green-500/30'
                    };
                    return (
                      <div key={idx} className="flex items-start gap-3 p-3 bg-slate-900/50 rounded-xl">
                        <span className={`px-2 py-0.5 text-xs rounded border ${priorityColors[priority]}`}>
                          {priority}
                        </span>
                        <span className="text-gray-300">{text}</span>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Action Items */}
            {insight.action_items?.length > 0 && (
              <div className="bg-gradient-to-br from-green-600/20 to-green-600/5 rounded-2xl p-6 border border-green-500/30">
                <h3 className="text-lg font-bold text-white mb-4 flex items-center gap-2">
                  <CheckCircle2 className="w-5 h-5 text-green-400" /> Action Items
                </h3>
                <ul className="space-y-2">
                  {insight.action_items.map((item, idx) => (
                    <li key={idx} className="flex items-center gap-3 text-gray-300">
                      <input type="checkbox" className="w-4 h-4 rounded bg-slate-700 border-slate-600" />
                      {item}
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {/* Alerts */}
            {insight.alerts?.length > 0 && (
              <div className="bg-gradient-to-br from-red-600/20 to-red-600/5 rounded-2xl p-6 border border-red-500/30">
                <h3 className="text-lg font-bold text-white mb-4 flex items-center gap-2">
                  <AlertTriangle className="w-5 h-5 text-red-400" /> Alerts
                </h3>
                <ul className="space-y-2">
                  {insight.alerts.map((alert, idx) => (
                    <li key={idx} className="flex items-start gap-2 text-red-300">
                      <AlertTriangle className="w-4 h-4 mt-1" />
                      {alert}
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {/* Past Insights */}
            {pastInsights.length > 1 && (
              <div className="bg-slate-800/50 rounded-2xl p-6">
                <h3 className="text-lg font-bold text-white mb-4 flex items-center gap-2">
                  <Calendar className="w-5 h-5 text-gray-400" /> Past Insights
                </h3>
                <div className="space-y-2">
                  {pastInsights.filter(p => p.id !== insight.id).slice(0, 4).map(past => (
                    <button
                      key={past.id}
                      onClick={() => setInsight(past)}
                      className="w-full flex items-center justify-between p-3 bg-slate-900/50 rounded-xl hover:bg-slate-900 transition-all"
                    >
                      <div className="flex items-center gap-3">
                        <span className={`text-lg font-bold ${getScoreColor(past.overall_score)}`}>
                          {past.overall_score}
                        </span>
                        <span className="text-gray-400 capitalize">{past.period}</span>
                      </div>
                      <span className="text-gray-500 text-sm">
                        {new Date(past.generated_at).toLocaleDateString()}
                      </span>
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Share Modal */}
      {showShareModal && (
        <div className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-slate-800 rounded-2xl p-6 max-w-md w-full">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-xl font-bold text-white flex items-center gap-2">
                <Share2 className="w-5 h-5 text-violet-400" /> Share Insight
              </h2>
              <button onClick={() => setShowShareModal(false)} className="text-gray-400">
                <X className="w-5 h-5" />
              </button>
            </div>
            
            <p className="text-gray-400 text-sm mb-4">
              Share this health insight with your doctor or family members.
            </p>
            
            <div className="flex items-center gap-2 p-3 bg-slate-900 rounded-lg mb-4">
              <Link2 className="w-5 h-5 text-gray-400" />
              <input
                type="text"
                value={shareLink}
                readOnly
                className="flex-1 bg-transparent text-white text-sm outline-none"
              />
              <button
                onClick={copyShareLink}
                className="p-2 bg-violet-500 rounded-lg text-white"
              >
                {copied ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
              </button>
            </div>
            
            <div className="flex gap-3">
              <button
                onClick={() => setShowShareModal(false)}
                className="flex-1 py-3 bg-slate-700 text-white rounded-xl"
              >
                Close
              </button>
              <button
                onClick={exportPDF}
                className="flex-1 py-3 bg-violet-500 text-white rounded-xl flex items-center justify-center gap-2"
              >
                <Download className="w-4 h-4" /> Download PDF
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default HealthInsights;
